import { TranslationKey } from '../../../../types/translation/TranslationKey';
import { useQuery } from '@apollo/client';
import React, { useContext, useMemo } from 'react';
import { useState } from 'react';
import { TranslationContext } from '../../../../context/TranslationContext';
import {
  GetTranslationsResponse,
  GetTranslationsVariables,
  TranslationQueries,
} from '../../../../graphql/translations/translation.graphql';
import { emptyTranslationRecord, TranslationRecord, TranslationUtils } from '../../../../types/translation/common';
import { Translation, TranslationLanguage } from '../../../../types/translation/translation';
import { englishTranslations, t } from '../../../../types/translation/Translator';
import Paper from '../../../../VentoryUI/components/common/Paper';
import Table, { TableHeader } from '../../../components/Table';
import { TranslationFilter } from '../../../../VentoryUI/components/filters/TranslationFilter';
import LoadingPackage from '../../Common/LoadingPackage';
import TranslationRecordModal from './Modals/TranslationRecordModal';
import Checkbox from '../../../../VentoryUI/components/common/Checkbox/Checkbox';
import { SuperUserContext } from '../../../../context/SuperUserContext';
import { SuperUserRole } from '../../../../types/superUser';
import { toFilterString } from '../../../../util/string.util';
import { LanguageSelect } from '../../../components/Dropdown';
import { SearchFilter } from '../../../components/Filter';
import { NoItemsFound } from '../../../components/NoItems';

const whitelistedKeys = new Set(['cm', 'dm', 'g', 'kg', 'lb', 'lpn', 'm', 'mm', 'originalEquipmentManufacturer', 'oz']);

export default function AdministratorTranslationsPane() {
  const { translations, setTranslations } = useContext(TranslationContext);

  const { superUser } = useContext(SuperUserContext);
  const [allowedLanguages, setAllowedLanguages] = useState<TranslationLanguage[]>([]);

  const { loading, refetch } = useQuery<GetTranslationsResponse, GetTranslationsVariables>(TranslationQueries.getAll, {
    onCompleted: async response => {
      for (const t of response.translations) {
        translations.set(t.language, Translation.fromJson(t.language, t.translations));
        if (superUser?.role === SuperUserRole.translator && !new Set(superUser.languages).has(t.language)) {
          continue;
        }
        allowedLanguages.push(t.language);
      }

      setAllowedLanguages([...allowedLanguages]);
      setTranslations(new Map(translations));
    },
  });

  const [selectedLanguage, setSelectedLanguage] = useState<TranslationLanguage | undefined>(undefined);
  const selectedTranslation = useMemo(() => {
    if (allowedLanguages.length === 1) setSelectedLanguage(allowedLanguages[0]);
    return translations.get(selectedLanguage || '');
  }, [translations, selectedLanguage, allowedLanguages]);

  const [items, setItems] = useState<TranslationKey[]>(TranslationUtils.allKeys);

  const [untranslatedOnly, setUntranslatedOnly] = useState<boolean>(false);
  const allItems = useMemo(() => {
    if (!untranslatedOnly) return TranslationUtils.allKeys;
    return TranslationUtils.allKeys.filter(key => {
      if (whitelistedKeys.has(key)) return false;
      if (!selectedTranslation?.translations[key]) return true;
      return (
        toFilterString(selectedTranslation?.translations[key].singular.label) ===
          toFilterString('Undefined Translation') ||
        (selectedLanguage !== TranslationLanguage.en &&
          selectedTranslation?.translations[key].singular.label ===
            translations.get(TranslationLanguage.en)?.translations[key].singular.label)
      );
    });
  }, [untranslatedOnly, selectedTranslation]);

  const [translationRecordModalProps, setTranslationRecordModalProps] = useState<{
    open: boolean;
    record?: TranslationRecord;
    key?: TranslationKey;
  }>({ open: false });

  const filter = new TranslationFilter(englishTranslations, selectedTranslation?.translations);

  const headers: TableHeader<TranslationKey>[] = [
    { key: 'key', name: 'Key', text: (item: TranslationKey) => item },
    {
      key: 'language',
      name: t(TranslationKey.language).singular.upper,
      text: (item: TranslationKey) => selectedTranslation?.translations[item]?.singular?.upper || '',
    },
    {
      key: 'english',
      name: 'English',
      text: (item: TranslationKey) => englishTranslations[item]?.singular?.upper || '',
    },
  ];

  if (loading) {
    return (
      <Paper>
        <div className='flex h-full w-full items-center justify-center'>
          <LoadingPackage />
        </div>
      </Paper>
    );
  }

  return (
    <>
      {translationRecordModalProps.open &&
      translationRecordModalProps.record &&
      translationRecordModalProps.key &&
      selectedTranslation ? (
        <TranslationRecordModal
          open={translationRecordModalProps.open}
          translationRecord={translationRecordModalProps.record}
          setOpen={open => setTranslationRecordModalProps({ ...translationRecordModalProps, open })}
          translation={selectedTranslation}
          translationKey={translationRecordModalProps.key}
        />
      ) : null}

      <div className='content-x content-b-screen flex h-full flex-col gap-4'>
        <div className='flex items-center justify-between gap-2'>
          <SearchFilter items={allItems} setItems={setItems} filter={filter} />
          <div className='flex items-center gap-2'>
            <Checkbox
              label={t(TranslationKey.untranslatedOnly).singular.label}
              value={untranslatedOnly}
              onChange={v => setUntranslatedOnly(v)}
            />

            <LanguageSelect
              items={superUser?.role === SuperUserRole.translator ? superUser.languages : undefined}
              label={false}
              language={selectedLanguage}
              setLanguage={setSelectedLanguage}
            />
          </div>
        </div>

        {selectedTranslation ? (
          <Table
            headers={headers}
            items={items}
            totalItemCount={selectedTranslation ? Object.keys(selectedTranslation.translations).length : 0}
            filter={filter}
            onClick={item => {
              if (!selectedTranslation) return;
              setTranslationRecordModalProps({
                open: true,
                record: selectedTranslation.translations[item as TranslationKey]?.toRecord() || emptyTranslationRecord,
                key: item as TranslationKey,
              });
            }}
          />
        ) : (
          <div className='flex flex-grow items-center justify-center'>
            <NoItemsFound title={'No translation language selected!'} />
          </div>
        )}
      </div>
    </>
  );
}
